import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Typography, Paper, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, TextField, Autocomplete,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import SidebarLayout from '../components/SidebarLayout';

interface SummaryItem {
  statVideoId: number;
  userId: number;
  username: string;
  videoUrl: string;
  videoTitle: string;
  recordCount: number;
  firstPageView: number | null;
  firstTime: string | null;
  firstClientId: string;
  lastPageView: number | null;
  lastTime: string | null;
  lastClientId: string;
  delta: number | null;
}

interface UserItem {
  id: number;
  username: string;
}

interface SummaryMeta {
  idCount: number;
  totalRecords: number;
  totalDelta: number;
}

const StatVideoDashboard = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<SummaryItem[]>([]);
  const [meta, setMeta] = useState<SummaryMeta>({ idCount: 0, totalRecords: 0, totalDelta: 0 });
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState<UserItem[]>([]);
  const [status, setStatus] = useState('');
  /** 空字符串 = 全部（不限时间） */
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  const filterParams = useCallback(() => ({
    userId: selectedUser || undefined,
    status: status === '' ? undefined : status,
    // 未选时间不传参 → 后端查全部；传入后最早/最新都落在该区间内
    startTime: startTime || undefined,
    endTime: endTime || undefined,
  }), [selectedUser, status, startTime, endTime]);

  const fetchUsers = async (search = '') => {
    const resp = await axios.get('/api/users', { params: { search } });
    setUsers(resp.data.users || []);
  };

  const fetchSummary = useCallback(async () => {
    const resp = await axios.get('/api/bili/stat-video-summary', { params: filterParams() });
    setItems(resp.data.items || []);
    setMeta(resp.data.summary || { idCount: 0, totalRecords: 0, totalDelta: 0 });
  }, [filterParams]);

  useEffect(() => {
    if (user?.role === 'admin') fetchUsers().catch(console.error);
  }, [user]);

  useEffect(() => { fetchSummary().catch(console.error); }, [fetchSummary]);

  const formatTime = (t?: string | null) => {
    if (!t) return '-';
    return new Date(t).toLocaleString('zh-CN', { hour12: false });
  };

  return (
    <SidebarLayout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>小视频播放量统计</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            ['小视频 ID 数', meta.idCount, 'text.primary'],
            ['记录条数', meta.totalRecords, 'text.primary'],
            ['播放量增量合计', meta.totalDelta, 'primary.main'],
          ].map(([label, value, color]) => (
            <Grid item xs={6} sm={4} key={label as string}>
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="h4" color={color as string}>
                    {typeof value === 'number' && label === '播放量增量合计' && value > 0 ? `+${value}` : value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {user?.role === 'admin' && (
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={users}
                getOptionLabel={(option) => option.username}
                value={users.find((u) => u.id === selectedUser) || null}
                onChange={(_, newValue) => setSelectedUser(newValue ? newValue.id : '')}
                inputValue={userSearch}
                onInputChange={(_, newInputValue) => {
                  setUserSearch(newInputValue);
                  fetchUsers(newInputValue).catch(console.error);
                }}
                renderInput={(params) => <TextField {...params} label="选择用户" variant="outlined" fullWidth />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                clearOnBlur={false}
              />
            </Grid>
          )}
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>类型</InputLabel>
              <Select value="stat" label="类型" disabled>
                <MenuItem value="stat">统计小视频</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>状态</InputLabel>
              <Select
                value={status}
                label="状态"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="1">成功</MenuItem>
                <MenuItem value="0">失败</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              label="开始时间"
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={startTime ? '最早记录 ≥ 此时间' : '默认全部'}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <TextField
              label="结束时间"
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={endTime ? '最新记录 ≤ 此时间' : '默认全部'}
              fullWidth
            />
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          时间范围：{!startTime && !endTime
            ? '全部'
            : `${startTime ? startTime.replace('T', ' ') : '不限'} ~ ${endTime ? endTime.replace('T', ' ') : '不限'}`}
          （「最早/最新」均取该范围内的第一条与最后一条）
        </Typography>

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>小视频 ID</TableCell>
                  <TableCell>用户</TableCell>
                  <TableCell>视频标题</TableCell>
                  <TableCell>视频地址</TableCell>
                  <TableCell>最早播放量</TableCell>
                  <TableCell>最早时间</TableCell>
                  <TableCell>最新播放量</TableCell>
                  <TableCell>最新时间</TableCell>
                  <TableCell>增量</TableCell>
                  <TableCell>记录数</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      暂无统计小视频记录
                    </TableCell>
                  </TableRow>
                ) : items.map((r) => (
                  <TableRow key={`${r.userId}-${r.statVideoId}`} hover>
                    <TableCell>
                      <Chip size="small" label={`ID #${r.statVideoId}`} color="secondary" variant="outlined" />
                    </TableCell>
                    <TableCell>{r.username || r.userId}</TableCell>
                    <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.videoTitle}>
                      {r.videoTitle || '-'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.videoUrl}>
                      {r.videoUrl ? (
                        <Box component="a" href={r.videoUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main' }}>
                          {r.videoUrl}
                        </Box>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{r.firstPageView != null ? r.firstPageView : '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(r.firstTime)}</TableCell>
                    <TableCell>
                      <Typography component="span" fontWeight={600}>
                        {r.lastPageView != null ? r.lastPageView : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(r.lastTime)}</TableCell>
                    <TableCell>
                      {r.delta == null ? '-' : (
                        <Chip
                          size="small"
                          label={r.delta >= 0 ? `+${r.delta}` : String(r.delta)}
                          color={r.delta > 0 ? 'success' : r.delta < 0 ? 'error' : 'default'}
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>{r.recordCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          按小视频 ID（关联用户）分组；在筛选时间范围内取最早一条与最新一条的「页面播放量」对比。
        </Typography>
      </Container>
    </SidebarLayout>
  );
};

export default StatVideoDashboard;
